/**
 * Chapter 2 Optimized Layout Component
 * 
 * Compact, no-scroll reading experience specifically designed for Chapter 2.
 * Everything fits in one viewport: image, text, and navigation controls.
 * Uses efficient space utilization and compact navigation bar.
 * Follows the exact same optimized pattern as Chapter1Optimized.
 * 
 * 🎬 VIDEO LAYOUT UPDATE (12/14/2025):
 * - Video container: h-4/5 (80% height) - TALLER to show more content
 * - Text section: Plain text (no box), black font on cream background
 */

import { motion } from "motion/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CueInteraction } from "@/components/cue-interaction";
import { Chapter2CueChoice } from "@/components/chapter-2-cue-choice";
import { ForestJourneyMap } from "@/components/forest-journey-map";
import { Chapter2OpeningScene } from "@/components/chapter-2-opening-scene";
import { MaxTrippingScene } from "@/components/max-tripping-scene";
import { BellaHelpingMaxScene } from "@/components/bella-helping-max-scene";
import { ChameleonScene } from "@/components/chameleon-scene";
import { MaxPolkaDottedPizzaScene } from "@/components/max-polka-dotted-pizza-scene";
import { CamilleExplainingFeelingsScene } from "@/components/camille-explaining-feelings-scene";
import { MilaReaderScene } from "@/components/mila-reader-scene";
import { MaxThoughtfulBreathingScene } from "@/components/max-thoughtful-breathing-scene";
import { AllFriendsNamingFeelingsScene } from "@/components/all-friends-naming-feelings-scene";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { saveReadingPosition } from "@/lib/bookmark";
import { updateCurrentPosition } from "@/lib/platform-tracking";
import { startSession, sendAnalytics } from "@/lib/analytics/client";
import { ArrowLeft, ArrowRight, Home, BookOpen, Rainbow } from "lucide-react";

// Chapter 2 spreads data
// Chapter 2 spreads data with Cloudflare Stream video IDs (8 videos for 8 story spreads, no video for cue page)
const chapter2Data = {
  title: "Chapter 2: The Colorful Chameleon",
  subtitle: "Emotional Management",
  cue: "Rainbow Tail",
  cueIcon: Rainbow,
  cueColor: "text-purple-600",
  bgColor: "bg-purple-50",
  spreads: [
    {
      spread: 0,
      section: "map",
      isMapPage: true, // NEW: Interactive forest map showing journey path
    },
    {
      spread: 1,
      section: "story",
      visualComponent: "Chapter2OpeningScene",
      videoId: "08cb088c3b677ec9b25cbe6ab9448884",
      text: "The sun was shining bright as Mila and her friends followed the next twist on their mystery map. Mila skipped ahead, eager to see what adventure waited."
    },
    {
      spread: 2,
      section: "story",
      visualComponent: "MaxTrippingScene",
      videoId: "fa2e60cf6262db15566bf38087e23505",
      text: "Suddenly, Pip the porcupine tripped over a root and tumbled into a pile of leaves.\n\n\"Ouch!\" Pip squeaked, rubbing his paw. His face scrunched up, and Mila saw tears in his eyes."
    },
    {
      spread: 3,
      section: "story",
      visualComponent: "BellaHelpingMaxScene",
      videoId: "8b84fea2dddf9713f07565b4e77fba9b",
      text: "Bella the bunny tried to help, but Pip pushed her away. \"I'm fine!\" he snapped, even though he didn't look fine at all.\n\nMila felt a swirl of feelings—worried for Pip, annoyed at his snapping, and a little bit confused."
    },
    {
      spread: 4,
      section: "story",
      visualComponent: "ChameleonScene",
      videoId: "34ea54442f0826aaf735186df43aa16b",
      text: "Just then, a bright flash of color caught her eye. A chameleon was perched on a nearby branch, her skin changing from green to blue to yellow.\n\n\"Hello there,\" said the chameleon gently. \"My name is Camille. I change colors when I feel different things. Sometimes I'm happy and sunny yellow, sometimes I'm sad and blue, and sometimes I'm mad and red.\""
    },
    {
      spread: 5,
      section: "story",
      visualComponent: "MaxPolkaDottedPizzaScene",
      videoId: "a0d3ae7c8e613fa28a0e4d01b7be5c9b",
      text: "Pip perked up, wiping his nose. \"If my feelings showed up on my fur, I'd look like a polka-dotted pizza! Or maybe a rainbow donut with extra sprinkles!\"\n\nBella giggled. \"Or a porcupine disco ball!\""
    },
    {
      spread: 6,
      section: "story",
      visualComponent: "CamilleExplainingFeelingsScene",
      videoId: "e9c830614edb23dcfc291d2963feb651",
      text: "Camille smiled. \"Even if your feelings don't show on the outside, it's important to notice them on the inside. When I feel a big feeling, I take a slow breath and name it. That helps me figure out what I need.\""
    },
    {
      spread: 7,
      section: "cue",
      visualComponent: "MilaReaderScene",
      // NO VIDEO for cue page - Interactive Rainbow Tail activity
      cue: "Rainbow Tail",
      cueText: "Time to name your feeling out loud!"
    },
    {
      spread: 8,
      section: "story",
      visualComponent: "MaxThoughtfulBreathingScene",
      videoId: "a4d509ae5b41d5594658dcd183d2f387",
      text: "Pip sniffled and took a deep breath. \"I guess I feel hurt and embarrassed. Maybe my fur's turning invisible!\"\n\n\"That's a brave thing to say,\" said Camille. \"When you notice your feeling and name it, it can start to change—just like my colors.\""
    },
    {
      spread: 9,
      section: "story",
      visualComponent: "AllFriendsNamingFeelingsScene",
      videoId: "5766f4b6dc48b16d42dfeed868703b2d",
      text: "The friends each tried naming their feelings out loud. Mila felt worried and a little bit mad. Bella felt sad for Pip. Grandpa Tortoise said he felt patient and ready to help. Together, they took a slow, deep breath, just like Camille. Pip's face relaxed, and he even managed a small smile.\n\nPip grinned. \"Next time I feel all mixed-up, I'll ask for help instead of screaming. Or maybe I'll just turn into a rainbow donut and surprise everyone!\"\n\n\"Whatever color you feel,\" Camille said, \"remember, all feelings are welcome here.\""
    }
  ]
};

// Visual component mapping
const visualComponents = {
  Chapter2OpeningScene,
  MaxTrippingScene,
  BellaHelpingMaxScene,
  ChameleonScene,
  MaxPolkaDottedPizzaScene,
  CamilleExplainingFeelingsScene,
  MilaReaderScene,
  MaxThoughtfulBreathingScene,
  AllFriendsNamingFeelingsScene
};

interface Chapter2OptimizedProps {
  onBackToContents: () => void;
}

export default function Chapter2Optimized({ onBackToContents }: Chapter2OptimizedProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Initialize spread from URL parameter or default to 0
  const spreadParam = searchParams.get('spread');
  const initialSpread = spreadParam ? parseInt(spreadParam) : 0;
  const [currentSpread, setCurrentSpread] = useState(initialSpread);
  const [completedCues, setCompletedCues] = useState<Set<string>>(new Set());

  const handleCueComplete = () => {
    setCompletedCues(prev => new Set(prev).add('rainbow-tail-cue'));
  };

  // Analytics tracking
  const startTimeRef = useRef<number>(Date.now());
  const sessionIdRef = useRef<string | null>(null);
  const interactionsRef = useRef<string[]>([]);
  const pagesVisitedRef = useRef<Set<number>>(new Set());

  const totalSpreads = chapter2Data.spreads.length;
  const currentSpreadData = chapter2Data.spreads[currentSpread];

  // NO AUTO-ADVANCE - User must click glowing icon to start reading
  
  // Reset spread when coming from bookmark
  useEffect(() => {
    const spreadParam = searchParams.get('spread');
    const targetSpread = spreadParam ? parseInt(spreadParam) : 0;
    setCurrentSpread(targetSpread);
  }, [searchParams]);
  
  // Start analytics session on component mount
  useEffect(() => {
    const initSession = async () => {
      try {
        // Start ONE session for this reading session
        const sessionId = await startSession();
        sessionIdRef.current = sessionId;
        startTimeRef.current = Date.now();
        pagesVisitedRef.current.add(currentSpread + 1);
        
        console.log('📊 SINGLE session started for Chapter 2:', sessionId);
      } catch (error) {
        console.warn('Failed to start analytics session:', error);
      }
    };
    
    initSession();
    
    // Basic tracking initialization
    updateCurrentPosition(2, currentSpread + 1);
    
    // ✅ FIX #2: NO CLEANUP - Let TOC handle session ending
    // The chapter component should NOT end the session when unmounting
    // because the user might be navigating to another chapter or Activities
    // Only the TOC page should call endReadingSession() when truly exiting the book
    return () => {
      console.log('📊 Chapter cleanup - Session continues (TOC will handle ending)');
    };
  }, []);

  // Save bookmark when spread changes
  useEffect(() => {
    const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
    
    pagesVisitedRef.current.add(currentSpread + 1);
    
    saveReadingPosition(2, currentSpread + 1);
    updateCurrentPosition(2, currentSpread);
    
    // 🎯 Send analytics to PostgreSQL using new API
    const sendAnalyticsAsync = async () => {
      try {
        await sendAnalytics(2, currentSpread + 1);
        console.log(`✅ Enhanced analytics sent to PostgreSQL - Chapter 2, Spread ${currentSpread + 1}`);
      } catch (error) {
        console.warn('Failed to send enhanced analytics:', error);
      }
    };
    sendAnalyticsAsync();
    
    console.log(`📊 Chapter 2 - Spread ${currentSpread + 1} viewed (${timeSpent}s, ${interactionsRef.current.length} interactions)`);
  }, [currentSpread]);

  const handleNextSpread = useCallback(() => {
    interactionsRef.current.push(`navigate_next_spread_${currentSpread + 1}`);
    
    if (currentSpread < totalSpreads - 1) {
      setCurrentSpread(currentSpread + 1);
    } else {
      interactionsRef.current.push('navigate_to_activities');
      navigate("/book/chapter/2/activity");
    }
  }, [currentSpread, totalSpreads, navigate]);

  const handlePrevSpread = useCallback(() => {
    interactionsRef.current.push(`navigate_previous_spread_${currentSpread + 1}`);
    
    if (currentSpread > 0) {
      setCurrentSpread(currentSpread - 1);
    } else {
      navigate("/book/contents");
    }
  }, [currentSpread, navigate]);

  const renderVisual = (componentName: string) => {
    const Component = visualComponents[componentName as keyof typeof visualComponents];
    if (Component) {
      return <Component className="w-full h-full object-contain" />;
    }
    return null;
  };

  // Full-screen MAP page (Spread 0 - Forest Journey Map)
  if (currentSpreadData?.section === "map") {
    return (
      <div className="fixed inset-0 z-50">
        <ForestJourneyMap
          currentChapter={2}
        />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-purple-50 to-pink-50">
      
      {/* COMPACT HEADER - TRANSLUCENT */}
      <div className="bg-white/30 backdrop-blur-sm border-b border-purple-200/50 px-4 py-2 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          
          {/* Left: Back + Chapter Title */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-forest-600 to-golden-600 rounded-full flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-sm font-bold text-forest-800">Dira Books</div>
              </div>
            </div>
            <div className="text-forest-400">•</div>
            <Button
              onClick={onBackToContents}
              variant="ghost"
              size="sm"
              className="text-forest-700 px-2 py-1 h-8 hover:bg-transparent hover:text-forest-600"
            >
              <BookOpen className="w-4 h-4 mr-1" />
              Contents
            </Button>
            <div className="text-forest-400">•</div>
            <h1 className="text-lg font-bold text-forest-700">
              Chapter 2: The Colorful Chameleon <span className="text-sm font-normal text-forest-600">• Emotional Management</span>
            </h1>
          </div>
          
          {/* Right: Badge + Activities */}
          <div className="flex items-center gap-2">
            <Badge 
              className="text-xs px-2 py-1 border bg-white/50 backdrop-blur-sm" 
              style={{ 
                color: '#5D4E37', 
                borderColor: '#E6D2AA' 
              }}
            >
              🌈 Rainbow Tail
            </Badge>
            
            <Button
              onClick={() => {
                interactionsRef.current.push('click_activities_button');
                navigate("/book/chapter/2/activity");
              }}
              variant="outline"
              size="sm"
              className="text-xs px-2 py-1 h-8 border-purple-300 text-purple-700 bg-white/20 hover:bg-transparent hover:text-forest-600"
            >
              🎯 Activities
            </Button>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {currentSpreadData?.section === "cue" ? (
          /* CUE PAGE - FULL SCREEN GRADIENT BACKGROUND LIKE CHAPTER 3 */
          <div className="flex-1 relative overflow-hidden bg-gradient-to-br from-purple-300 via-violet-400 to-purple-500">
            
            {/* Main Cue Box - Centered */}
            <div className="relative z-10 h-full flex items-center justify-center p-8">
              <motion.div 
                className="bg-cream-50 border-4 border-purple-400 rounded-3xl p-12 text-center mx-auto max-w-2xl"
                style={{ 
                  backgroundColor: '#FDF6E3',
                  borderColor: '#9333EA'
                }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                {/* Celebratory Emojis at Top */}
                <div className="text-5xl mb-8">
                  🌈 ✨ 🌈
                </div>
                
                {/* Magic Cue Found Title */}
                <h2 className="text-4xl font-bold mb-10" style={{ color: '#7C3AED' }}>
                  ✨ Magic Cue Found! ✨
                </h2>
                
                {/* Instruction Text */}
                <div className="text-3xl leading-relaxed font-medium mb-6" style={{ color: '#9333EA' }}>
                  Click the glowing Rainbow Tail in the corner to try the activity!
                </div>
              </motion.div>
            </div>

            {/* Cue Interaction */}
            <div className="absolute top-8 right-8 z-20">
              <Chapter2CueChoice
                onComplete={handleCueComplete}
                isCompleted={completedCues.has('rainbow-tail-cue')}
              />
            </div>
          </div>
        ) : (
          /* STORY PAGES - SPLIT LAYOUT */
          <>
            {/* TOP: VIDEO/IMAGE SECTION - Taller (70%) with 200% zoom, narrower max-width */}
            <div className="h-[70%] bg-transparent overflow-hidden relative">
              <div className="h-full p-2 flex items-center justify-center">
                {/* PRIORITY: Cloudflare Stream video if available */}
                {currentSpreadData?.videoId ? (
                  <div className="w-full h-full rounded-lg overflow-hidden bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 max-w-5xl mx-auto">
                    <iframe
                      key={currentSpreadData.videoId}
                      src={`https://iframe.videodelivery.net/${currentSpreadData.videoId}?autoplay=true&muted=true&controls=false&preload=auto&fit=crop`}
                      style={{ 
                        border: 'none', 
                        width: '170%',
                        height: '170%',
                        position: 'relative',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        pointerEvents: 'none'
                      }}
                      allow="autoplay; encrypted-media"
                      title={`Chapter 2 Spread ${currentSpread + 1} Video`}
                      onLoad={() => {
                        console.log(`✅ Cloudflare Stream iframe loaded - Chapter 2, Spread ${currentSpread + 1}, ID: ${currentSpreadData.videoId}`);
                      }}
                    />
                  </div>
                ) : currentSpreadData?.visualComponent ? (
                  renderVisual(currentSpreadData.visualComponent)
                ) : null}
              </div>
            </div>
            
            {/* BOTTOM: TEXT SECTION - Bigger text (text-xl), constrained to video width, left-aligned like a book */}
            <div className="h-[30%] flex flex-col px-8 py-6">
              <div className="flex-1 flex items-center">
                <div className="text-xl leading-relaxed text-gray-900 whitespace-pre-line max-w-5xl mx-auto" style={{ textAlign: 'left' }}>
                  {currentSpreadData?.text?.split('\n\n').map((paragraph, index) => (
                    <p key={index} className={index > 0 ? 'mt-4' : ''} style={{ textAlign: 'left' }}>
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Minimal Navigation Controls */}
      <div className="border-t border-gray-200 p-4">
        <div className="flex justify-between items-center py-0 px-2">
          {/* Previous Button - Ultra Minimal */}
          <Button
            onClick={handlePrevSpread}
            disabled={currentSpread === 0}
            variant="ghost"
            size="sm"
            className="text-forest-600 disabled:opacity-50 px-2 py-0 h-6 hover:bg-transparent hover:text-forest-600"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            Prev
          </Button>

          {/* Chapter Progress with Dots */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-forest-600">Chapter Progress</span>
            <div className="flex gap-1">
              {chapter2Data.spreads.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSpread(index)}
                  className={`w-2 h-2 rounded-full  ${
                    index === currentSpread
                      ? 'bg-forest-500'
                      : 'bg-forest-200'
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-forest-600">{currentSpread} of {chapter2Data.spreads.length - 1}</span>
          </div>

          {/* Next Button - Ultra Minimal */}
          <Button
            onClick={handleNextSpread}
            variant="ghost"
            size="sm"
            className="text-forest-600 px-2 py-0 h-6 hover:bg-transparent hover:text-forest-600"
          >
            {currentSpread === totalSpreads - 1 ? "Activities" : "Next"}
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </div>

    </div>
  );
}
